import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Paginator } from 'primereact/paginator';
import '../../css/Pages.css';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';

const SyncToTdkt = () => {
    const toast = useRef(null);
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);

    const [first, setFirst] = useState(0);
    const rows = 20;

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = useMemo(() => (currentUser.RoleName || '').toLowerCase(), [currentUser.RoleName]);

    const isAdmin = currentUser.RoleId === 1 || userRoleName === 'quản trị viên';

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const fetchLogs = async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tdkt-sync-log`, {
                method: 'GET',
                headers: authHeaders
            });

            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem('user');
                localStorage.removeItem('accessToken');
                window.location.href = '/login';
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setLogs(result);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu lịch sử đồng bộ!' });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const executeSync = async () => {
        setIsSyncing(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/trigger-tdkt-sync`, {
                method: 'POST',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({
                    userId: currentUser.Id,
                    email: currentUser.Email,
                    departmentId: currentUser.DepartmentId
                })
            });

            if (response.ok) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Đã gửi lệnh đồng bộ dữ liệu sang hệ thống TDKT thành công!',
                    life: 5000
                });
                fetchLogs(true);
            } else {
                const err = await response.json();
                toast.current?.show({
                    severity: 'error',
                    summary: 'Lỗi',
                    detail: err.message || 'Lỗi khi gửi lệnh đồng bộ!'
                });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Mất kết nối đến máy chủ NCKH!' });
        } finally {
            setIsSyncing(false);
        }
    };

    const handleSyncClick = () => {
        if (!isAdmin) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Từ chối',
                detail: 'Chỉ Quản trị viên hệ thống mới có quyền chốt dữ liệu đồng bộ!',
                life: 3000
            });
            return;
        }

        confirmDeleteDialog({
            header: 'Xác nhận đồng bộ hệ thống',
            message: 'Bạn có chắc chắn muốn chốt dữ liệu hiện tại và đẩy sang hệ thống Thi đua Khen thưởng (TDKT)? Quá trình này sẽ đồng bộ Đề tài, Sáng kiến và Sách/Giáo trình đã được phê duyệt.',
            accept: executeSync
        });
    };

    const paginatedLogs = useMemo(() => logs.slice(first, first + rows), [logs, first, rows]);

    const onPageChange = (event) => {
        setFirst(event.first);
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '---';
        try {
            const date = new Date(dateStr);
            return date.toLocaleString('vi-VN', {
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                day: '2-digit', month: '2-digit', year: 'numeric'
            });
        } catch (e) {
            return dateStr;
        }
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>ĐỒNG BỘ DỮ LIỆU THI ĐUA - KHEN THƯỞNG</h2>
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', textAlign: 'center' }}>
                <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '48px', color: '#3498db', marginBottom: '15px' }}></i>
                <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Đẩy dữ liệu sang hệ thống Thi đua Khen thưởng</h3>
                <p style={{ color: '#666', marginBottom: '25px', maxWidth: '600px', margin: '0 auto 25px auto', lineHeight: '1.5' }}>
                    Chức năng này sẽ đóng gói toàn bộ danh sách Đề tài, Sáng kiến, và Sách/Giáo trình có trạng thái <strong>"Đã duyệt"</strong> và báo hiệu cho hệ thống Thi đua - Khen thưởng tiến hành kéo dữ liệu về. Vui lòng đảm bảo các thông tin đã được kiểm duyệt kỹ lưỡng trước khi thao tác.
                </p>

                <button
                    onClick={handleSyncClick}
                    disabled={isSyncing}
                    style={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        padding: '12px 25px',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: isSyncing ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        opacity: isSyncing ? 0.7 : 1
                    }}
                >
                    <i className={`fa-solid ${isSyncing ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                    {isSyncing ? 'Đang gửi lệnh đồng bộ...' : 'Cho phép đồng bộ dữ liệu sang hệ thống Thi đua - Khen thưởng'}
                </button>
            </div>

            <div className="table-card" style={{ overflowX: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', paddingBottom: '10px', position: 'relative' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
                    <p className="sub-title" style={{ margin: 0, fontWeight: 'bold', color: '#555' }}>
                        <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: '8px' }}></i>
                        LỊCH SỬ LƯU VẾT ĐỒNG BỘ
                    </p>
                </div>

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <i className="fa-solid fa-circle-notch fa-spin fa-2x" style={{ color: '#3498db' }}></i>
                    </div>
                ) : logs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '50px 20px', color: '#888' }}>
                        <i className="fa-regular fa-folder-open" style={{ fontSize: '48px', color: '#bdc3c7', marginBottom: '15px' }}></i>
                        <p style={{ margin: 0 }}>Chưa có lịch sử đồng bộ nào được ghi nhận.</p>
                    </div>
                ) : (
                    <>
                        <table className="custom-table" style={{ width: '100%', minWidth: '900px' }}>
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th width="5%" style={{ textAlign: 'center' }}>STT</th>
                                    <th width="25%">Người dùng thực hiện</th>
                                    <th width="25%">Email</th>
                                    <th width="25%">Đơn vị</th>
                                    <th width="20%" style={{ textAlign: 'center' }}>Thời gian đồng bộ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedLogs.map((log, index) => {
                                    const actualIndex = first + index + 1;
                                    return (
                                        <tr key={log.Id || index}>
                                            <td style={{ textAlign: 'center' }}>{actualIndex}</td>
                                            <td style={{ fontWeight: '500', color: '#2c3e50' }}>{log.FullName}</td>
                                            <td>{log.Email}</td>
                                            <td>{log.DepartmentName || '---'}</td>
                                            <td style={{ textAlign: 'center', fontWeight: '500' }}>
                                                {formatDateTime(log.SyncTime)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {logs.length > rows && (
                            <div style={{ marginTop: '15px', borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                                <Paginator
                                    first={first}
                                    rows={rows}
                                    totalRecords={logs.length}
                                    onPageChange={onPageChange}
                                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                    style={{ background: 'transparent', border: 'none' }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SyncToTdkt;